import React from "react";
import { useTranslation } from "react-i18next";
import {
  FaFileInvoiceDollar,
  FaReceipt,
  FaCreditCard,
  FaFileAlt,
  FaExclamationTriangle,
  FaUniversity,
  FaHandHoldingUsd,
} from "react-icons/fa";
import { AppScreen, AppHeader } from "../components/app";
import { appColors, appRadius, appShadows, appSpacing, appType } from "../utils/appTheme";
import IconBox from "../components/app/IconBox";
import { useBuilding } from "../context/BuildingContext";

/**
 * Hub Finanzas (screen 03 del handoff). Solo navegación — cero lógica.
 * Rutea a las pantallas planas existentes vía setSelected.
 *
 * Presentación: título único en el AppHeader (azul Habitia) + grilla 2×2 de
 * tiles VERTICALES (icono en IconBox arriba, título, subtítulo y una línea de
 * estado/afordancia coloreada abajo). No hay estado financiero en este hub, así
 * que la línea inferior es una afordancia de navegación (no inventa montos ni
 * estados de datos).
 */
// Textos (name/subtitle/status) viven en el namespace i18n "finanzas", indexados
// por `key` del tile: tiles.<key>.{name,subtitle,status}.
const TILES = [
  { key: "cuentas", icon: <FaFileInvoiceDollar />, statusColor: appColors.blue },
  { key: "expensas", icon: <FaReceipt />, statusColor: appColors.blue },
  { key: "mispagos", icon: <FaCreditCard />, statusColor: appColors.ink3 },
  { key: "documentos", icon: <FaFileAlt />, statusColor: appColors.ink3 },
  { key: "multasresidente", icon: <FaExclamationTriangle />, statusColor: appColors.ink3 },
  { key: "gastosvecinos", icon: <FaHandHoldingUsd />, statusColor: appColors.blue },
];

const FinanceTile = ({ tile, onClick }) => {
  const { t } = useTranslation("finanzas");
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 8,
        minHeight: 148,
        minWidth: 0,
        width: "100%",
        textAlign: "left",
        background: appColors.surface,
        border: `1px solid ${appColors.line2}`,
        borderRadius: appRadius.tile,
        boxShadow: appShadows.soft,
        padding: 16,
        cursor: "pointer",
      }}
    >
      <IconBox icon={tile.icon} />
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ ...appType.tileName, overflowWrap: "anywhere" }}>{t(`tiles.${tile.key}.name`)}</span>
        <span style={{ ...appType.bodySm, overflowWrap: "anywhere" }}>{t(`tiles.${tile.key}.subtitle`)}</span>
      </span>
      <span
        style={{
          ...appType.bodySm,
          marginTop: "auto",
          color: tile.statusColor,
          overflowWrap: "anywhere",
        }}
      >
        {t(`tiles.${tile.key}.status`)}
      </span>
    </button>
  );
};

// Tile solo para propietarios: estado de cuenta del edificio (saldos + movimientos).
const OWNER_TILE = {
  key: "cuentasedificio",
  icon: <FaUniversity />,
  statusColor: appColors.blue,
};

const Finanzas = ({ setSelected, goBack }) => {
  const { t } = useTranslation("finanzas");
  const { selectedBuilding, userUnitsInBuilding } = useBuilding();

  // "Cuentas del edificio" (saldos + movimientos de todos) es para propietarios
  // VIGENTES: el ex-propietario (unidad histórica, edificio de solo lectura) lee
  // solo sus pagos (reglas 0D) y la pantalla le quedaría vacía.
  const isOwner =
    !selectedBuilding?.isReadOnly &&
    (userUnitsInBuilding || []).some((u) => u.userRole === "Propietario" && !u.isHistorical);
  // "Cuentas del edificio" va después de "Estado de cuenta" (índice 0), solo a propietarios.
  const tiles = isOwner ? [TILES[0], OWNER_TILE, ...TILES.slice(1)] : TILES;

  return (
    <AppScreen
      header={
        <AppHeader
          title={t("header.title")}
          subtitle={selectedBuilding?.name || t("header.subtitle")}
          onBack={goBack}
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: appSpacing.gridGap,
        }}
      >
        {tiles.map((t) => (
          <FinanceTile
            key={t.key}
            tile={t}
            onClick={() => setSelected && setSelected(t.key)}
          />
        ))}
      </div>
    </AppScreen>
  );
};

export default Finanzas;
